import { loadJSON, saveJSON } from '../../../utils/tabStorage'
import { standardId } from '../../../utils/standardRef'

// Pure guide-filter logic for the Code page's guides pane, mirroring the comments
// filter. A window's guide filter is plain serializable data; `makeGuideMatcher`
// turns it into a single predicate.
//
// Facets (combined by an Any/All `matchMode`, like the comments/nav popover):
//   - topics  one or more of the user's topics (OR within the group)
//   - items   one or more linked items (OR within the group), optionally rolled
//             down to their descendants
// A free-text `search` (title / description) is always AND-applied on top.

export const GUIDE_SORTS = [
  { id: 'latest', label: 'Latest' },
  { id: 'oldest', label: 'Oldest' },
  { id: 'title', label: 'Title (A–Z)' },
]

const VALID_SORTS = new Set(GUIDE_SORTS.map((o) => o.id))

export function emptyGuideFilter() {
  return {
    topics: [], // [{ _id, name }]
    items: [], // [{ _id, label, standard, order }]
    includeDescendants: false,
    search: '',
    matchMode: 'or', // 'or' | 'and'
    sort: 'latest',
  }
}

export function coerceGuideFilter(raw) {
  const base = emptyGuideFilter()
  if (!raw || typeof raw !== 'object') return base
  return {
    topics: Array.isArray(raw.topics) ? raw.topics : [],
    items: Array.isArray(raw.items) ? raw.items : [],
    includeDescendants: Boolean(raw.includeDescendants),
    search: typeof raw.search === 'string' ? raw.search : '',
    matchMode: raw.matchMode === 'and' ? 'and' : 'or',
    sort: VALID_SORTS.has(raw.sort) ? raw.sort : 'latest',
  }
}

export function isGuideFilterActive(filter) {
  const f = filter || emptyGuideFilter()
  return Boolean(f.topics?.length || f.items?.length || f.search?.trim())
}

// A guide's linked item matches a selected filter item when it's the same item,
// or (with descendants) a descendant — same standard AND (same order, or the
// selected order is in the item's ancestor path).
function itemMatchesSelection(item, sel, includeDescendants) {
  if (!item) return false
  if (standardId(sel.standard) && standardId(item.standard) && standardId(item.standard) !== standardId(sel.standard)) return false
  if (String(item._id) === String(sel._id)) return true
  if (sel.order != null && item.order === sel.order) return true
  if (
    includeDescendants &&
    sel.order != null &&
    Array.isArray(item.path) &&
    item.path.includes(sel.order)
  ) {
    return true
  }
  return false
}

function matchesItems(guide, items, includeDescendants) {
  const linked = guide.items || []
  return items.some((sel) => linked.some((it) => itemMatchesSelection(it, sel, includeDescendants)))
}

function matchesTopics(guide, topicIds) {
  const set = new Set((guide.topics || []).map((t) => String(t?._id ?? t)))
  return [...topicIds].some((id) => set.has(id))
}

function matchesSearch(guide, q) {
  const hay = `${guide.title || ''} ${guide.description || ''} ${guide.name || ''}`.toLowerCase()
  return hay.includes(q)
}

export function makeGuideMatcher(filter) {
  const f = coerceGuideFilter(filter)
  const topicIds = new Set(f.topics.map((t) => String(t?._id ?? t)))
  const q = f.search.trim().toLowerCase()
  const { items, includeDescendants, matchMode } = f

  const combine = (checks) => {
    if (!checks.length) return true
    return matchMode === 'and' ? checks.every(Boolean) : checks.some(Boolean)
  }

  const match = (guide) => {
    if (q && !matchesSearch(guide, q)) return false
    const checks = []
    if (topicIds.size) checks.push(matchesTopics(guide, topicIds))
    if (items.length) checks.push(matchesItems(guide, items, includeDescendants))
    return combine(checks)
  }

  return { active: isGuideFilterActive(f), match }
}

export function sortGuides(list, sort) {
  const arr = [...list]
  if (sort === 'title') {
    return arr.sort((a, b) =>
      (a.title || a.name || '').localeCompare(b.title || b.name || '', undefined, {
        sensitivity: 'base',
      })
    )
  }
  const dir = sort === 'oldest' ? 1 : -1
  return arr.sort((a, b) => dir * (new Date(a.updatedAt) - new Date(b.updatedAt)))
}

// --- per-window persistence ------------------------------------------------
const STORE_KEY = 'ct-code-guides-v1'

function readStore() {
  try {
    const parsed = loadJSON(STORE_KEY)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function loadGuideFilter(windowId) {
  if (typeof window === 'undefined' || !windowId) return emptyGuideFilter()
  return coerceGuideFilter(readStore()[windowId])
}

export function saveGuideFilter(windowId, filter) {
  if (typeof window === 'undefined' || !windowId) return
  try {
    const store = readStore()
    store[windowId] = filter
    saveJSON(STORE_KEY, store)
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}
